/**
 * Pure arena (dish) math: the concave-bowl center-pull and the rim/ring-out
 * boundary (DESIGN §3.1). No Phaser — operates on plain position/velocity.
 */
import type { Vec2 } from './vec.ts';
import { sub, scale, len, normalize, dot } from './vec.ts';

export interface ArenaConfig {
  center: Vec2;
  /** Radius of the rim/lip. Crossing it = leaving the flat dish. */
  rimRadius: number;
  /** Spring constant for the pull toward center (force per unit distance). */
  pullK: number;
  /**
   * Outward radial speed (units/sec) above which crossing the rim is a
   * ring-out instead of a wall-bounce.
   */
  ringOutSpeed: number;
  /** Velocity retained after a wall bounce (0..1). */
  bounceRestitution: number;
}

/**
 * The bowl's restoring force toward the center, proportional to distance.
 * Returns a force vector (acceleration before mass).
 */
export function centerPull(pos: Vec2, cfg: ArenaConfig): Vec2 {
  const toCenter = sub(cfg.center, pos);
  return scale(toCenter, cfg.pullK);
}

export interface BoundaryResult {
  /** True if the bey left the arena (a ring-out). */
  ringOut: boolean;
  /** Corrected position (clamped to the rim on a bounce; unchanged otherwise). */
  pos: Vec2;
  /** Corrected velocity (reflected inward on a bounce; unchanged otherwise). */
  vel: Vec2;
  /** True if a wall bounce occurred this step. */
  bounced: boolean;
}

/**
 * Resolve a bey against the rim.
 *
 * - Inside the rim: nothing happens.
 * - Crossing the rim fast while eligible to ring out: ring-out.
 * - Otherwise (slow, or not eligible): bounce back in (wall-ride).
 *
 * `canRingOut` lets the caller forbid ring-outs that aren't caused by an
 * opponent's knock — a bey riding the wall under its own momentum just bounces.
 */
export function resolveBoundary(
  pos: Vec2,
  vel: Vec2,
  cfg: ArenaConfig,
  canRingOut = true,
): BoundaryResult {
  const radial = sub(pos, cfg.center);
  const distance = len(radial);

  if (distance <= cfg.rimRadius) {
    return { ringOut: false, pos, vel, bounced: false };
  }

  const outwardDir = normalize(radial);
  const outwardSpeed = dot(vel, outwardDir);

  // Flew over the lip with enough outward momentum (and eligible) -> ring-out.
  if (canRingOut && outwardSpeed > cfg.ringOutSpeed) {
    return { ringOut: true, pos, vel, bounced: false };
  }

  // Otherwise wall-ride: clamp to the rim and reflect the outward velocity in.
  const clampedPos: Vec2 = {
    x: cfg.center.x + outwardDir.x * cfg.rimRadius,
    y: cfg.center.y + outwardDir.y * cfg.rimRadius,
  };
  // Remove outward component, then add a damped inward component (bounce).
  const reflected: Vec2 = {
    x: vel.x - outwardDir.x * outwardSpeed * (1 + cfg.bounceRestitution),
    y: vel.y - outwardDir.y * outwardSpeed * (1 + cfg.bounceRestitution),
  };
  return { ringOut: false, pos: clampedPos, vel: reflected, bounced: true };
}
